// WeekViewMobileByEmployee.jsx
import "./WeekViewMobileByEmployee.scss";
import "./WeekViewMobile.scss";

import { useEffect, useMemo, useRef, useState } from "react";

import { displayName } from "../../../../../commons/util/HallAppUtil";

function WeekViewMobileByEmployee({
  currentDate: initialDate,
  events = [],
  onDayClick,
  eventClick,
  employees = [],
  selectedEmployeeId,
  onlyHoursWorked = false,
  intervalMinutes: intervalFromProps,
  closeDiary = [],
  onEmptySlotClick
}) {
  const scrollRef = useRef(null);

  const intervalMinutes = intervalFromProps ?? 30;
  const slotHeightPx = 60;
  const displayStepMinutes = intervalMinutes === 60 ? 60 : intervalMinutes;
  const pxPerMinute = slotHeightPx / displayStepMinutes;

  const minutesToHHmm = (mins) => {
    if (mins == null || !Number.isFinite(mins)) return "";
    const v = clamp(Math.round(mins), 0, 24 * 60 - 1);
    const h = pad2(Math.floor(v / 60));
    const m = pad2(v % 60);
    return `${h}:${m}`;
  };

  // ✅ desktop breakpoint
  const [isDesktop, setIsDesktop] = useState(() =>
    typeof window !== "undefined" ? window.matchMedia("(min-width: 992px)").matches : false
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    const mq = window.matchMedia("(min-width: 992px)");
    const onChange = () => setIsDesktop(mq.matches);

    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);

    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", onChange);
      else mq.removeListener(onChange);
    };
  }, []);

  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const date = new Date(initialDate);
    const day = date.getDay();
    date.setDate(date.getDate() - day);
    date.setHours(0, 0, 0, 0);
    return date;
  });

  const [selectedDay, setSelectedDay] = useState(initialDate);

  useEffect(() => {
    const d = new Date(initialDate);
    const day = d.getDay();
    const weekStart = new Date(d);
    weekStart.setDate(d.getDate() - day);
    weekStart.setHours(0, 0, 0, 0);

    setCurrentWeekStart(weekStart);
    setSelectedDay(d);
  }, [initialDate]);

  const monthNames = [
    "Janeiro",
    "Fevereiro",
    "Março",
    "Abril",
    "Maio",
    "Junho",
    "Julho",
    "Agosto",
    "Setembro",
    "Outubro",
    "Novembro",
    "Dezembro",
  ];

  const getWeekDays = () => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(currentWeekStart);
      date.setDate(currentWeekStart.getDate() + i);
      days.push(date);
    }
    return days;
  };

  const weekDays = getWeekDays();

  const goToPreviousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);

    const newSelectedDay = new Date(selectedDay);
    newSelectedDay.setDate(newSelectedDay.getDate() - 7);
    setSelectedDay(newSelectedDay);

    onDayClick?.(newSelectedDay);
  };

  const goToNextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);

    const newSelectedDay = new Date(selectedDay);
    newSelectedDay.setDate(newSelectedDay.getDate() + 7);
    setSelectedDay(newSelectedDay);

    onDayClick?.(newSelectedDay);
  };

  const goToToday = () => {
    const today = new Date();
    const day = today.getDay();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - day);
    weekStart.setHours(0, 0, 0, 0);
    setCurrentWeekStart(weekStart);
    setSelectedDay(today);
    onDayClick?.(today);
  };

  useEffect(() => {
    // mobile: centraliza no dia selecionado
    const selectedDayIndex = weekDays.findIndex(
      (day) => day.toDateString() === selectedDay.toDateString()
    );
    if (scrollRef.current && selectedDayIndex !== -1) {
      const dayElement = scrollRef.current.children[selectedDayIndex];
      dayElement?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWeekStart]);

  const isToday = (date) => new Date().toDateString() === date.toDateString();
  const isSelected = (date) => date.toDateString() === selectedDay.toDateString();

  const isCurrentWeek = () => {
    const today = new Date();
    const todayWeekStart = new Date(today);
    const day = today.getDay();
    todayWeekStart.setDate(today.getDate() - day);
    todayWeekStart.setHours(0, 0, 0, 0);
    return currentWeekStart.getTime() === todayWeekStart.getTime();
  };

  // ===== helpers =====
  const isBlank = (v) => v == null || String(v).trim() === "";
  const pick = (...vals) => vals.find((v) => !isBlank(v)) ?? "";

  const toMinutes = (hhmm) => {
    if (!hhmm) return null;
    const [h, m] = String(hhmm).split(":").map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return null;
    return h * 60 + m;
  };

  const weekKeyFromDate = (date) => {
    const map = ["SUNDAY", "MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY"];
    return map[date.getDay()];
  };

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));
  const floorToStep = (min) => Math.floor(min / displayStepMinutes) * displayStepMinutes;
  const ceilToStep = (min) => Math.ceil(min / displayStepMinutes) * displayStepMinutes;

  const pad2 = (n) => String(n).padStart(2, "0");
  const dateKeyYYYYMMDD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

  const parseISODate = (iso) => {
    if (!iso) return null;
    const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
    if ([y, m, d].some((n) => Number.isNaN(n))) return null;
    const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
    dt.setHours(0, 0, 0, 0);
    return dt;
  };

  const isBetweenInclusive = (dayDate, startISO, endISO) => {
    const day = new Date(dayDate);
    day.setHours(0, 0, 0, 0);

    const s = parseISODate(startISO);
    const e = parseISODate(endISO || startISO);
    if (!s || !e) return false;

    const start = s.getTime();
    const end = e.getTime();
    const t = day.getTime();

    const min = Math.min(start, end);
    const max = Math.max(start, end);

    return t >= min && t <= max;
  };

  const blockTitleFromType = (type) => {
    const t = String(type || "").toUpperCase();
    if (t.includes("FERIA")) return "Férias";
    if (t.includes("FERIAD")) return "Feriado";
    if (t.includes("FOLGA")) return "Folga";
    if (t.includes("BLOCK")) return "Bloqueado";
    return "Bloqueado";
  };

  const formatRangeLabel = (startMin, endMin) => {
    const h1 = Math.floor(startMin / 60);
    const m1 = startMin % 60;
    const h2 = Math.floor(endMin / 60);
    const m2 = endMin % 60;
    return `${pad2(h1)}:${pad2(m1)}–${pad2(h2)}:${pad2(m2)}`;
  };

  const getWeekPeriod = () => {
    const firstDay = weekDays[0];
    const lastDay = weekDays[6];

    if (firstDay.getMonth() === lastDay.getMonth()) {
      return `${firstDay.getDate()} - ${lastDay.getDate()} de ${monthNames[firstDay.getMonth()]}`;
    }
    return `${firstDay.getDate()} de ${monthNames[firstDay.getMonth()]} - ${lastDay.getDate()} de ${monthNames[lastDay.getMonth()]
      }`;
  };

  /**
   * ✅ Ajustado:
   * - suporta semanas via weeksConfigMap (normalizado no pai)
   * - suporta backend novo/antigo + typos
   * - fallback legado startTime/endTime
   */
  const getWorkIntervals = (employee, dayDate) => {
    const weekKey = weekKeyFromDate(dayDate);

    const cfgFromMap = employee?.weeksConfigMap?.[weekKey];
    const cfg = cfgFromMap || employee?.weeksConfig?.find((w) => w.week === weekKey);

    if (!cfg) return [];

    const startM = pick(cfg.morning?.start, cfg.startTimeMorning, cfg.startTimeTimeMorning);
    const endM = pick(cfg.morning?.end, cfg.endTimeMorning, cfg.endTimeTimeMorning);

    const startA = pick(
      cfg.afternoon?.start,
      cfg.startTimeAfternoon,
      cfg.startTimeAftnon,
      cfg.startTimeAfternon
    );
    const endA = pick(
      cfg.afternoon?.end,
      cfg.endTimeAfternoon,
      cfg.endTimeTimeAfternoon,
      cfg.endTimeAftnon,
      cfg.endTimeAfternon
    );

    const hasNew = !isBlank(startM) || !isBlank(endM) || !isBlank(startA) || !isBlank(endA);

    if (hasNew) {
      const intervals = [];

      const sm = toMinutes(startM);
      const em = toMinutes(endM);
      if (sm != null && em != null && em > sm) {
        intervals.push({ label: "Manhã", startMin: sm, endMin: em, startTime: startM, endTime: endM });
      }

      const sa = toMinutes(startA);
      const ea = toMinutes(endA);
      if (sa != null && ea != null && ea > sa) {
        intervals.push({ label: "Tarde", startMin: sa, endMin: ea, startTime: startA, endTime: endA });
      }

      intervals.sort((a, b) => a.startMin - b.startMin);
      return intervals;
    }

    // legado startTime/endTime
    const start = pick(cfg.startTime);
    const end = pick(cfg.endTime);

    const s = toMinutes(start);
    const e = toMinutes(end);

    if (s != null && e != null && e > s) {
      return [{ label: "Expediente", startMin: s, endMin: e, startTime: start, endTime: end }];
    }

    return [];
  };

  // Eventos do dia selecionado
  const dayEvents = useMemo(() => {
    return events.filter((event) => {
      const eventDate = new Date(event.start);
      return eventDate.toDateString() === selectedDay.toDateString();
    });
  }, [events, selectedDay]);

  const isOverlapping = (eventA, eventB) => {
    const startA = new Date(eventA.start);
    const endA = new Date(eventA.end);
    const startB = new Date(eventB.start);
    const endB = new Date(eventB.end);
    return startA < endB && startB < endA;
  };

  const groupOverlappingEvents = (eventsList) => {
    const sortedEvents = [...eventsList].sort((a, b) => new Date(a.start) - new Date(b.start));
    const groups = [];
    sortedEvents.forEach((event) => {
      let placed = false;
      for (const group of groups) {
        if (group.some((e) => isOverlapping(e, event))) {
          group.push(event);
          placed = true;
          break;
        }
      }
      if (!placed) groups.push([event]);
    });
    return groups;
  };

  const isEventSmall = (event) => {
    const start = new Date(event.start);
    const end = new Date(event.end);
    const durationMinutes = (end - start) / (1000 * 60);
    return durationMinutes < 30;
  };

  // =========================
  // ✅ Calendário Desktop
  // =========================
  const DesktopCalendar = () => {
    const dayNames = ["D", "S", "T", "Q", "Q", "S", "S"];

    const [currentMonth, setCurrentMonth] = useState(() => {
      const d = new Date(selectedDay);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    });

    useEffect(() => {
      const d = new Date(selectedDay);
      setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }, [selectedDay]);

    const goPrevMonth = () => {
      const d = new Date(currentMonth);
      d.setMonth(d.getMonth() - 1);
      setCurrentMonth(d);
    };

    const goNextMonth = () => {
      const d = new Date(currentMonth);
      d.setMonth(d.getMonth() + 1);
      setCurrentMonth(d);
    };

    const goTodayMonth = () => {
      const t = new Date();
      setCurrentMonth(new Date(t.getFullYear(), t.getMonth(), 1));
      setSelectedDay(t);
      onDayClick?.(t);
    };

    const calendarDays = useMemo(() => {
      const year = currentMonth.getFullYear();
      const month = currentMonth.getMonth();

      const firstDay = new Date(year, month, 1);
      const firstDayOfWeek = firstDay.getDay();

      const lastDay = new Date(year, month + 1, 0);
      const lastDate = lastDay.getDate();

      const prevMonthLastDay = new Date(year, month, 0).getDate();

      const days = [];

      for (let i = firstDayOfWeek - 1; i >= 0; i--) {
        days.push({
          date: new Date(year, month - 1, prevMonthLastDay - i),
          isCurrentMonth: false,
        });
      }

      for (let i = 1; i <= lastDate; i++) {
        days.push({
          date: new Date(year, month, i),
          isCurrentMonth: true,
        });
      }

      const remainingDays = 42 - days.length;
      for (let i = 1; i <= remainingDays; i++) {
        days.push({
          date: new Date(year, month + 1, i),
          isCurrentMonth: false,
        });
      }

      return days;
    }, [currentMonth]);

    const isCurrentMonthNow = () => {
      const t = new Date();
      return t.getFullYear() === currentMonth.getFullYear() && t.getMonth() === currentMonth.getMonth();
    };

    return (
      <aside className="week-view-mobile-by-employee__desktop-calendar">
        <div className="week-view-mobile-by-employee__desktop-calendar-header">
          <button className="week-view-mobile-by-employee__cal-nav" onClick={goPrevMonth} aria-label="Mês anterior">
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>

          <div className="week-view-mobile-by-employee__cal-title">
            <div className="week-view-mobile-by-employee__cal-month">
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </div>
            {!isCurrentMonthNow() && (
              <button className="week-view-mobile-by-employee__cal-today" onClick={goTodayMonth}>
                Hoje
              </button>
            )}
          </div>

          <button className="week-view-mobile-by-employee__cal-nav" onClick={goNextMonth} aria-label="Próximo mês">
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            </svg>
          </button>
        </div>

        <div className="week-view-mobile-by-employee__cal-weekdays">
          {dayNames.map((d, i) => (
            <div key={i} className="week-view-mobile-by-employee__cal-weekday">
              {d}
            </div>
          ))}
        </div>

        <div className="week-view-mobile-by-employee__cal-grid">
          {calendarDays.map((cell, idx) => {
            const today = isToday(cell.date);
            const selected = isSelected(cell.date);

            return (
              <button
                key={idx}
                type="button"
                className={
                  "week-view-mobile-by-employee__cal-day " +
                  (!cell.isCurrentMonth ? "week-view-mobile-by-employee__cal-day--other " : "") +
                  (today ? "week-view-mobile-by-employee__cal-day--today " : "") +
                  (selected ? "week-view-mobile-by-employee__cal-day--selected " : "")
                }
                onClick={() => {
                  setSelectedDay(cell.date);

                  // sincroniza semana (importante)
                  const d = new Date(cell.date);
                  const dow = d.getDay();
                  const ws = new Date(d);
                  ws.setDate(d.getDate() - dow);
                  ws.setHours(0, 0, 0, 0);
                  setCurrentWeekStart(ws);

                  onDayClick?.(cell.date);
                }}
                aria-label={cell.date.toLocaleDateString("pt-BR")}
              >
                {cell.date.getDate()}
              </button>
            );
          })}
        </div>
      </aside>
    );
  };

  // ✅ Coluna por funcionário
  const EmployeeScheduleColumn = ({ employee }) => {
    const contentRef = useRef(null);
    const eventsColRef = useRef(null);

    const empEvents = useMemo(
      () => dayEvents.filter((e) => Number(e.employeeId) === Number(employee.id)),
      [dayEvents, employee.id]
    );

    const handleEmptySlotClick = (e) => {
      // se clicou em evento ou bloqueio, não é slot vazio
      if (e.target.closest(".week-view-mobile__event")) return;
      if (e.target.closest(".week-view-mobile__close-block")) return;
      if (e.target.closest(".week-view-mobile__block")) return;

      const col = eventsColRef.current;
      const scroller = contentRef.current;
      if (!col || !scroller) return;

      const rect = col.getBoundingClientRect();
      const clickY = e.clientY - rect.top;

      const yInContent = clickY + (scroller.scrollTop || 0);

      const rawMinutes = rangeStartMin + yInContent / pxPerMinute;
      const snapped = floorToStep(rawMinutes);

      const hhmm = minutesToHHmm(snapped);
      if (!hhmm) return;

      const d = new Date(selectedDay);
      d.setHours(0, 0, 0, 0);

      const [hh, mm] = hhmm.split(":").map(Number);
      if (!Number.isFinite(hh) || !Number.isFinite(mm)) return;

      d.setHours(hh, mm, 0, 0);

      onEmptySlotClick?.({
        date: d,
        time: hhmm,
        minutes: snapped,
        employeeId: employee.id,
      });
    };

    const { rangeStartMin, rangeEndMin, workIntervals } = useMemo(() => {
      const DAY_START = 0;
      const DAY_END = 24 * 60;

      const intervals = getWorkIntervals(employee, selectedDay);

      let finalStart = DAY_START;
      let finalEnd = DAY_END;

      if (onlyHoursWorked) {
        const baseStart = intervals?.[0]?.startMin ?? null;
        const baseEnd = intervals?.[intervals.length - 1]?.endMin ?? null;

        if (baseStart != null && baseEnd != null && baseEnd > baseStart) {
          finalStart = baseStart;
          finalEnd = baseEnd;

          const BUFFER_MIN = displayStepMinutes;

          let minEvt = null;
          let maxEvt = null;

          for (const ev of empEvents) {
            const s = new Date(ev.start);
            const e = new Date(ev.end);
            const sMin = s.getHours() * 60 + s.getMinutes();
            const eMin = e.getHours() * 60 + e.getMinutes();
            if (minEvt == null || sMin < minEvt) minEvt = sMin;
            if (maxEvt == null || eMin > maxEvt) maxEvt = eMin;
          }

          if (minEvt != null && maxEvt != null) {
            finalStart = Math.min(finalStart, minEvt - BUFFER_MIN);
            finalEnd = Math.max(finalEnd, maxEvt + BUFFER_MIN);
          }

          finalStart = clamp(finalStart, DAY_START, DAY_END);
          finalEnd = clamp(finalEnd, DAY_START, DAY_END);

          finalStart = floorToStep(finalStart);
          finalEnd = ceilToStep(finalEnd);

          if (finalEnd <= finalStart) {
            finalStart = DAY_START;
            finalEnd = DAY_END;
          }
        }
      }

      return { rangeStartMin: finalStart, rangeEndMin: finalEnd, workIntervals: intervals };
    }, [employee, onlyHoursWorked, selectedDay, empEvents, displayStepMinutes]);

    const timeLabels = useMemo(() => {
      const arr = [];
      for (let m = rangeStartMin; m < rangeEndMin; m += displayStepMinutes) {
        const h = Math.floor(m / 60);
        const mm = m % 60;
        arr.push({ minutes: m, label: `${pad2(h)}:${pad2(mm)}` });
      }
      return arr;
    }, [rangeStartMin, rangeEndMin, displayStepMinutes]);

    const totalHeightPx = (rangeEndMin - rangeStartMin) * pxPerMinute;

    const getEventStyle = (event) => {
      const start = new Date(event.start);
      const end = new Date(event.end);

      const startMinutes = start.getHours() * 60 + start.getMinutes();
      const endMinutes = end.getHours() * 60 + end.getMinutes();

      const visibleStart = Math.max(startMinutes, rangeStartMin);
      const visibleEnd = Math.min(endMinutes, rangeEndMin);

      const duration = Math.max(0, visibleEnd - visibleStart);

      const top = (visibleStart - rangeStartMin) * pxPerMinute;
      const height = Math.max(8, duration * pxPerMinute);

      return { top: `${top}px`, height: `${height}px` };
    };

    const getCurrentTimePosition = () => {
      const now = new Date();
      const minutes = now.getHours() * 60 + now.getMinutes();
      return (minutes - rangeStartMin) * pxPerMinute;
    };

    const showCurrentTimeLine = (() => {
      if (!isSelected(new Date())) return false;
      const now = new Date();
      const minutes = now.getHours() * 60 + now.getMinutes();
      return minutes >= rangeStartMin && minutes <= rangeEndMin;
    })();

    // ✅ BLOQUEIOS (closeDiary)
    const dayKey = useMemo(() => dateKeyYYYYMMDD(selectedDay), [selectedDay]);

    const empBlocks = useMemo(() => {
      if (!Array.isArray(closeDiary) || closeDiary.length === 0) return [];

      const raw = closeDiary.filter(
        (b) =>
          Number(b?.employeeId) === Number(employee.id) &&
          isBetweenInclusive(selectedDay, b?.startDate, b?.endDate)
      );

      return raw
        .map((b) => {
          const sMin = toMinutes(b?.startTime);
          const eMin = toMinutes(b?.endTime);
          const hasRange = sMin != null && eMin != null && eMin > sMin;

          const desiredStart = hasRange ? sMin : rangeStartMin;
          const desiredEnd = hasRange ? eMin : rangeEndMin;

          const clampedStart = Math.max(rangeStartMin, Math.min(rangeEndMin, desiredStart));
          const clampedEnd = Math.max(rangeStartMin, Math.min(rangeEndMin, desiredEnd));

          if (clampedEnd <= clampedStart) return null;

          return {
            raw: b,
            title: blockTitleFromType(b?.type),
            startMin: clampedStart,
            endMin: clampedEnd,
            fullDay: !hasRange,
            label: hasRange ? formatRangeLabel(clampedStart, clampedEnd) : "Dia todo",
            __dayKey: dayKey,
          };
        })
        .filter(Boolean);
    }, [closeDiary, employee.id, selectedDay, rangeStartMin, rangeEndMin, dayKey]);

    // ✅ BLOQUEIO AUTOMÁTICO: fora do expediente + intervalo (manhã/tarde)
    const empWorkBlocks = useMemo(() => {
      const DAY_START = 0;
      const DAY_END = 24 * 60;

      const intervals = workIntervals || [];

      if (!intervals.length) {
        return [
          {
            __label: "Fora do horário de trabalho",
            __startMin: DAY_START,
            __endMin: DAY_END,
            __hasRange: false,
            startTime: "",
            endTime: "",
          },
        ];
      }

      const blocks = [];

      if (onlyHoursWorked) {
        for (let i = 0; i < intervals.length - 1; i++) {
          const a = intervals[i];
          const b = intervals[i + 1];
          if (b.startMin > a.endMin) {
            blocks.push({
              __label: "Intervalo",
              __startMin: a.endMin,
              __endMin: b.startMin,
              __hasRange: true,
              startTime: a.endTime || "—",
              endTime: b.startTime || "—",
            });
          }
        }
        return blocks;
      }

      const first = intervals[0];
      const last = intervals[intervals.length - 1];

      if (first.startMin > DAY_START) {
        blocks.push({
          __label: "Fora do horário de trabalho",
          __startMin: DAY_START,
          __endMin: first.startMin,
          __hasRange: true,
          startTime: "00:00",
          endTime: first.startTime || "—",
        });
      }

      for (let i = 0; i < intervals.length - 1; i++) {
        const a = intervals[i];
        const b = intervals[i + 1];
        if (b.startMin > a.endMin) {
          blocks.push({
            __label: "Intervalo",
            __startMin: a.endMin,
            __endMin: b.startMin,
            __hasRange: true,
            startTime: a.endTime || "—",
            endTime: b.startTime || "—",
          });
        }
      }

      if (last.endMin < DAY_END) {
        blocks.push({
          __label: "Fora do horário de trabalho",
          __startMin: last.endMin,
          __endMin: DAY_END,
          __hasRange: true,
          startTime: last.endTime || "—",
          endTime: "23:59",
        });
      }

      return blocks;
    }, [workIntervals, onlyHoursWorked]);

    const hasBlock = empBlocks.length > 0;

    useEffect(() => {
      if (!contentRef.current) return;

      if (showCurrentTimeLine) {
        const currentTimePosition = getCurrentTimePosition();
        const scrollPosition = currentTimePosition - 150;

        const t = setTimeout(() => {
          contentRef.current?.scrollTo({
            top: Math.max(0, scrollPosition),
            behavior: "smooth",
          });
        }, 80);

        return () => clearTimeout(t);
      }

      const t = setTimeout(() => {
        contentRef.current?.scrollTo({ top: 0, behavior: "auto" });
      }, 0);

      return () => clearTimeout(t);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      employee.id,
      selectedDay,
      rangeStartMin,
      rangeEndMin,
      displayStepMinutes,
      onlyHoursWorked,
      showCurrentTimeLine,
    ]);

    const eventGroups = groupOverlappingEvents(empEvents);
    const positionedEvents = [];
    eventGroups.forEach((group) => {
      const hasSmallEvent = group.some(isEventSmall);
      const groupSize = group.length;

      group.forEach((event, index) => {
        if (hasSmallEvent) {
          positionedEvents.push({ event, widthPercent: 100, leftPercent: 0, colorIndex: index });
        } else {
          positionedEvents.push({
            event,
            widthPercent: 100 / groupSize,
            leftPercent: (100 / groupSize) * index,
            colorIndex: 0,
          });
        }
      });
    });

    const blockPillText = useMemo(() => {
      if (!hasBlock) return null;
      const first = empBlocks[0];
      if (empBlocks.length === 1) return `${first.title} • ${first.label}`;
      return `${first.title} • ${first.label} +${empBlocks.length - 1}`;
    }, [hasBlock, empBlocks]);

    return (
      <div className="week-view-mobile-by-employee__employee-page">
        <div className="week-view-mobile-by-employee__employee-header">
          <div className="week-view-mobile-by-employee__employee-name">
            {displayName(employee.name) || employee.email}
          </div>

          <div className="week-view-mobile-by-employee__employee-subRow">
            <div className="week-view-mobile-by-employee__employee-sub">
              {empEvents.length} agendamento{empEvents.length !== 1 ? "s" : ""}
            </div>

            {hasBlock && (
              <div className="week-view-mobile-by-employee__block-pill" title="Agenda bloqueada">
                <span className="week-view-mobile-by-employee__block-pill-dot" />
                {blockPillText}
              </div>
            )}
          </div>
        </div>

        <div
          className="week-view-mobile__content week-view-mobile-by-employee__content"
          ref={contentRef}
          key={`${employee.id}-${displayStepMinutes}-${rangeStartMin}-${rangeEndMin}`}
        >
          <div className="week-view-mobile__time-column" style={{ height: `${totalHeightPx}px` }}>
            {timeLabels.map((t) => (
              <div
                key={t.minutes}
                className="week-view-mobile__time-slot"
                style={{ height: `${slotHeightPx}px` }}
              >
                <span className="week-view-mobile__time-label">{t.label}</span>
              </div>
            ))}
          </div>

          + <div
            ref={eventsColRef}
            className="week-view-mobile__events-column"
            style={{ height: `${totalHeightPx}px` }}
            onClick={handleEmptySlotClick}
          >            {timeLabels.map((t) => (
            <div
              key={t.minutes}
              className="week-view-mobile__hour-line"
              style={{ top: `${(t.minutes - rangeStartMin) * pxPerMinute}px` }}
            />
          ))}

            {/* ✅ BLOQUEIO: fora do horário + intervalo (manhã/tarde) */}
            {empWorkBlocks.map((b, idx) => {
              const startMin = clamp(b.__startMin, 0, 24 * 60);
              const endMin = clamp(b.__endMin, 0, 24 * 60);

              const visibleStart = clamp(startMin, rangeStartMin, rangeEndMin);
              const visibleEnd = clamp(endMin, rangeStartMin, rangeEndMin);

              const duration = Math.max(0, visibleEnd - visibleStart);
              const top = (visibleStart - rangeStartMin) * pxPerMinute;
              const height = Math.max(14, duration * pxPerMinute);

              if (!height || height <= 0) return null;

              const isInterval = String(b.__label || "").toLowerCase().includes("intervalo");

              return (
                <div
                  key={`workclose-${employee.id}-${dayKey}-${idx}`}
                  className="week-view-mobile__close-block"
                  style={{
                    top: `${top}px`,
                    height: `${height}px`,
                    position: "absolute",
                    left: 0,
                    width: "100%",
                    zIndex: 1,
                    pointerEvents: "none",
                    opacity: isInterval ? 0.85 : 1,
                  }}
                >
                  <div className="week-view-mobile__close-block__badge">
                    {isInterval ? "Intervalo •" : "Agenda bloqueada •"} {b.__label}
                    <span className="week-view-mobile__close-block__time">
                      {" "}
                      ({b.__hasRange ? `${b.startTime}–${b.endTime}` : "dia todo"})
                    </span>
                  </div>
                </div>
              );
            })}

            {/* ✅ BLOQUEIOS VISUAIS (closeDiary) */}
            {empBlocks.map((b, idx) => {
              const top = (b.startMin - rangeStartMin) * pxPerMinute;
              const height = Math.max(10, (b.endMin - b.startMin) * pxPerMinute);
              return (
                <div
                  key={`block-${employee.id}-${b.__dayKey}-${idx}`}
                  className="week-view-mobile__block"
                  style={{ top: `${top}px`, height: `${height}px` }}
                  aria-label={`Bloqueado: ${b.title} (${b.reason || b.label})`}
                  title={`${b.title} • ${b.raw?.reason || b.label}`}
                >
                  <div className="week-view-mobile__block-content">
                    <div className="week-view-mobile__block-title">
                      {b.title}
                      <span className="week-view-mobile__block-badge">BLOQUEADO</span>
                    </div>
                    <div className="week-view-mobile__block-sub">{b.raw?.reason || b.label}</div>
                  </div>
                </div>
              );
            })}

            {showCurrentTimeLine && (
              <div
                className="week-view-mobile__current-time-line"
                style={{ top: `${getCurrentTimePosition()}px` }}
              >
                <div className="week-view-mobile__current-time-dot" />
                <div className="week-view-mobile__current-time-bar" />
              </div>
            )}

            {positionedEvents.map(({ event, widthPercent, leftPercent, colorIndex }, index) => (
              <div key={`${event.id ?? "evt"}-${index}`} onClick={() => eventClick?.(event)}>
                <EventCard
                  event={event}
                  getEventStyle={getEventStyle}
                  style={{
                    width: `${widthPercent}%`,
                    left: `${leftPercent}%`,
                    position: "absolute",
                    boxSizing: "border-box",
                    paddingLeft: "4px",
                    paddingRight: "4px",
                    zIndex: 50 + colorIndex,
                  }}
                  colorIndex={colorIndex}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // mantém: se vier selectedEmployeeId, coloca ele primeiro na lista (sem remover os outros)
  const orderedEmployees = useMemo(() => {
    if (!selectedEmployeeId) return employees;
    const idx = employees.findIndex((e) => Number(e.id) === Number(selectedEmployeeId));
    if (idx <= 0) return employees;
    const copy = [...employees];
    const [first] = copy.splice(idx, 1);
    return [first, ...copy];
  }, [employees, selectedEmployeeId]);

  return (
    <div className={`week-view-mobile-by-employee ${isDesktop ? "week-view-mobile-by-employee--desktop" : ""}`}>
      {/* header topo: navegação da semana */}
      <div className="week-view-mobile__header">
        <div className="week-view-mobile__navigation">
          <button className="week-view-mobile__nav-btn" onClick={goToPreviousWeek}>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
            </svg>
          </button>

          <div className="week-view-mobile__week-info">
            <span className="week-view-mobile__week-period">{getWeekPeriod()}</span>
            {!isCurrentWeek() && (
              <button className="week-view-mobile__today-btn" onClick={goToToday}>
                Hoje
              </button>
            )}
          </div>

          <button className="week-view-mobile__nav-btn" onClick={goToNextWeek}>
            <svg width="20" height="20" viewBox="0 0 24 24">
              <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
            </svg>
          </button>
        </div>

        {/* ✅ mobile: dias (chips) */}
        {!isDesktop && (
          <div className="week-view-mobile__days" ref={scrollRef}>
            {weekDays.map((day, index) => (
              <DayEventCard
                key={index}
                day={day}
                isToday={isToday(day)}
                isSelected={isSelected(day)}
                onClick={() => {
                  onDayClick?.(day);
                  setSelectedDay(day);
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* ✅ desktop: calendário à esquerda + agenda à direita */}
      {isDesktop ? (
        <div className="week-view-mobile-by-employee__desktop-layout">
          <DesktopCalendar />

          <div className="week-view-mobile-by-employee__desktop-schedule">
            <div className="week-view-mobile-by-employee__horizontal">
              <div className="week-view-mobile-by-employee__row">
                {orderedEmployees.map((emp) => (
                  <EmployeeScheduleColumn key={emp.id} employee={emp} />
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : (
        // ✅ mobile/tablet: mantém como estava
        <div className="week-view-mobile-by-employee__horizontal">
          <div className="week-view-mobile-by-employee__row">
            {orderedEmployees.map((emp) => (
              <EmployeeScheduleColumn key={emp.id} employee={emp} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function DayEventCard({ day, isToday, isSelected, onClick }) {
  return (
    <div
      className={`week-view-mobile__day ${isToday ? "week-view-mobile__day--today" : ""} ${isSelected ? "week-view-mobile__day--selected" : ""
        }`}
      onClick={onClick}
    >
      <span className="week-view-mobile__day-name">
        {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"][day.getDay()]}
      </span>
      <span className="week-view-mobile__day-number">{day.getDate()}</span>
    </div>
  );
}

function EventCard({ event, getEventStyle, style, colorIndex = 0 }) {
  const eventRef = useRef(null);
  const [isCompact, setIsCompact] = useState(false);

  useEffect(() => {
    if (eventRef.current) setIsCompact(eventRef.current.offsetHeight < 50);
  }, []);

  const now = new Date();
  const start = new Date(event.start);
  const end = new Date(event.end);

  let statusClass = "upcoming";
  if (now >= start && now <= end) statusClass = "in-progress";
  else if (now > end) statusClass = "past";

  const stackedColors = ["#007bff", "#0056b3", "#3399ff", "#66b2ff"];

  const hasCommand = event.commandId != null;
  const isPaid = event.commandStatus === "PAID";
  const isPromotion = Boolean(event?.promotionName);

  // ✅ ASSINATURA
  const hasClub = Boolean(event?.hasClub);

  const backgroundColor =
    style.width === "100%" && !isPaid
      ? stackedColors[colorIndex % stackedColors.length]
      : undefined;

  const isInProgress = statusClass === "in-progress";

  return (
    <div
      ref={eventRef}
      className={
        `week-view-mobile__event ` +
        `week-view-mobile__event--${statusClass} ` +
        (isCompact ? "week-view-mobile__event--compact " : "") +
        (hasCommand ? "week-view-mobile__event--has-command " : "") +
        (isPaid ? "week-view-mobile__event--paid " : "") +
        (isInProgress ? "week-view-mobile__event--live " : "") +
        (isPromotion ? "week-view-mobile__event--promotion " : "") +
        (hasClub ? "week-view-mobile__event--club " : "")
      }
      style={{
        ...getEventStyle(event),
        ...style,
        backgroundColor: isInProgress
          ? "#16a34a"
          : isPaid
          ? "#16a34a"
          : backgroundColor || style.backgroundColor || "#667eea",
      }}
      title={hasClub ? "Cliente assinante do clube" : undefined}
    >
      {/* ⭐ Badge CLUBE */}
      {hasClub && (
        <div
          className="week-view-mobile__event-club-badge"
          title="Cliente assinante"
        >
          ⭐ Clube
        </div>
      )}

      {isPromotion && (
        <div
          className="week-view-mobile__event-promo-badge"
          title={`Promoção: ${event.promotionName}`}
        >
          🔥 Promo • {event.promotionName}
        </div>
      )}

      {hasCommand && (
        <div
          className={
            "week-view-mobile__event-command-badge " +
            (isPaid
              ? "week-view-mobile__event-command-badge--paid"
              : "")
          }
          title={
            isPaid
              ? `Comanda #${event.commandId} paga`
              : `Comanda #${event.commandId}`
          }
        >
          {isPaid ? "✔ Pago" : "🧾 Comanda"}
        </div>
      )}

      {isCompact ? (
        <div className="week-view-mobile__event-compact-content">
          <span className="week-view-mobile__event-title">
            {event.title}
          </span>

          {event.client && (
            <>
              {" "}
              -{" "}
              <span className="week-view-mobile__event-client">
                {event.client}
              </span>
            </>
          )}

          {hasClub && " • Clube"}

          {" - "}
          <span className="week-view-mobile__event-time">
            {start.toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            até{" "}
            {end.toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        </div>
      ) : (
        <>
          <div className="week-view-mobile__event-time">
            {start.toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
            })}{" "}
            até{" "}
            {end.toLocaleTimeString("pt-BR", {
              hour: "2-digit",
              minute: "2-digit",
            })}
          </div>

          <div className="week-view-mobile__event-title">
            {event.title}
            {hasClub && (
              <span className="week-view-mobile__event-club-inline">
                {" "}
                • Clube
              </span>
            )}
          </div>

          {event.client && (
            <div className="week-view-mobile__event-client">
              {event.client}
            </div>
          )}
        </>
      )}
    </div>
  );
}


export default WeekViewMobileByEmployee;
